import type { FC, ReactNode } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import Img from '@/components/generals/img'

interface ProductCardProps {
    product: Product
    sizes?: string
    className?: string
    reveal?: boolean
}

const ProductCard: FC<ProductCardProps> = ({
    product,
    sizes = '(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 50vw',
    className,
    reveal = true
}): ReactNode => {
    return (
        <article data-product-card className={cn('group', className)}>
            <Link
                href={`/shop/${product.slug}`}
                data-cursor='View'
                data-transition-label={product.name}
                className='block'
            >
                <div data-reveal={reveal ? 'image' : undefined} className='relative aspect-[3/4] overflow-hidden bg-coal'>
                    <Img
                        src={product.image}
                        alt={product.image_alt || product.name}
                        sizes={sizes}
                        className='transition-transform duration-[1.4s] ease-expo-out group-hover:scale-[1.04]'
                    />

                    <Img
                        src={product.detail_image}
                        alt={product.detail_image_alt || product.name}
                        sizes={sizes}
                        className='scale-[1.08] opacity-0 transition-[opacity,scale] duration-[1s] ease-expo-out group-hover:scale-100 group-hover:opacity-100'
                    />

                    {product.badge ? (
                        <span className='absolute left-2.5 top-2.5 bg-acid px-2 py-1 meta-mono text-ink md:left-3 md:top-3'>
                            <span data-mdlfy-cms={`products:${product.id}:badge`}>{product.badge}</span>
                        </span>
                    ) : null}

                    <span className='absolute inset-x-2.5 bottom-2.5 hidden translate-y-[calc(100%+14px)] items-center justify-between bg-bone px-3 py-2.5 label-mono text-ink transition-transform duration-700 ease-expo-out group-hover:translate-y-0 md:inset-x-3 md:bottom-3 md:flex'>
                        View piece

                        <ArrowUpRight aria-hidden='true' className='size-4' />
                    </span>
                </div>

                <div className='mt-3 flex items-start justify-between gap-3 md:mt-4'>
                    <div className='min-w-0'>
                        <h3 className='text-[15px] font-medium leading-snug tracking-[-0.01em] text-bone md:text-base'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                        </h3>

                        <p className='mt-1 meta-mono text-ash'>
                            <span data-mdlfy-cms={`products:${product.id}:category`}>{product.category}</span>
                        </p>
                    </div>

                    <p className='shrink-0 font-mono text-[13px] text-bone md:text-sm'>
                        <span data-mdlfy-cms={`products:${product.id}:price`}>{product.price}</span>
                    </p>
                </div>
            </Link>
        </article>
    )
}

export default ProductCard
